/**
 * Base Cache Strategy
 * 
 * Provides core cache-first functionality and shared infrastructure for all
 * data-specific cache strategies in the offline-first architecture.
 */

import { indexedDBService } from '@/services/offline/indexeddb';

/**
 * Cache strategy options
 */
export interface CacheStrategyOptions {
  maxAge?: number; // Maximum age in milliseconds before cache is considered stale
  staleWhileRevalidate?: boolean; // Return stale data while fetching fresh data
  backgroundSync?: boolean; // Enable background sync
  retryOnError?: boolean; // Retry failed requests
  maxRetries?: number; // Maximum number of retries
  retryDelay?: number; // Delay between retries in milliseconds
}

/**
 * Cache strategy result
 */
export interface CacheStrategyResult<T> {
  data: T | null;
  isStale: boolean;
  source: 'cache' | 'network' | 'sync';
  timestamp: number;
  error?: string;
}

/**
 * Base Cache Strategy Implementation
 * 
 * Provides core caching functionality that can be extended by specific
 * data type strategies for training, techniques, game plans, etc.
 */
export abstract class BaseStrategy {
  protected defaultOptions: Required<CacheStrategyOptions> = {
    maxAge: 5 * 60 * 1000, // 5 minutes
    staleWhileRevalidate: true,
    backgroundSync: true,
    retryOnError: true,
    maxRetries: 3,
    retryDelay: 1000
  };

  /**
   * Execute cache-first strategy for data fetching
   */
  async execute<T>(
    cacheKey: string,
    networkFetcher: () => Promise<T>,
    cacheFetcher: () => Promise<T | null>,
    options: CacheStrategyOptions = {}
  ): Promise<CacheStrategyResult<T>> {
    const opts = { ...this.defaultOptions, ...options };
    
    try {
      // Step 1: Try to get data from cache
      const cacheResult = await this.getCachedData<T>(cacheKey, cacheFetcher, opts);
      
      // Step 2: If cache hit and not stale, return cached data
      if (cacheResult.data && !cacheResult.isStale) {
        // Optionally trigger background sync
        if (opts.backgroundSync && navigator.onLine) {
          this.backgroundSync(cacheKey, networkFetcher, opts).catch(console.warn);
        }
        
        return cacheResult;
      }
      
      // Step 3: If cache is stale but staleWhileRevalidate is enabled, return stale data
      // and fetch fresh data in background
      if (cacheResult.data && opts.staleWhileRevalidate && navigator.onLine) {
        // Return stale data immediately
        const staleResult = { ...cacheResult, isStale: true };
        
        // Fetch fresh data in background
        this.revalidateInBackground(cacheKey, networkFetcher, opts).catch(console.warn);
        
        return staleResult;
      }
      
      // Step 4: No cache or cache is stale and no staleWhileRevalidate
      // Try to fetch from network
      if (navigator.onLine) {
        return await this.fetchFromNetwork<T>(cacheKey, networkFetcher, opts);
      }
      
      // Step 5: Offline and no cache, return null
      return {
        data: cacheResult.data,
        isStale: true,
        source: 'cache',
        timestamp: cacheResult.timestamp,
        error: 'No network connection and no cached data available'
      };
      
    } catch (error) {
      console.error('Cache-first strategy error:', error);
      
      // Try to return cached data as fallback
      try {
        const fallbackCache = await cacheFetcher();
        return {
          data: fallbackCache,
          isStale: true,
          source: 'cache',
          timestamp: Date.now(),
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      } catch {
        return {
          data: null,
          isStale: true,
          source: 'cache',
          timestamp: Date.now(),
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    }
  }

  /**
   * Get data from cache with staleness check
   */
  protected async getCachedData<T>(
    _cacheKey: string,
    cacheFetcher: () => Promise<T | null>,
    options: Required<CacheStrategyOptions>
  ): Promise<CacheStrategyResult<T>> {
    try {
      const cachedData = await cacheFetcher();
      
      if (!cachedData) {
        return {
          data: null,
          isStale: true,
          source: 'cache',
          timestamp: 0
        };
      }
      
      // Check if cache is stale based on sync metadata
      const metadata = await indexedDBService.syncMetadata.getAllMetadata();
      let lastSync = 0;
      
      if (metadata.length > 0) {
        const latestSync = metadata.reduce((latest, meta) => 
          meta.last_sync > latest ? meta.last_sync : latest, '');
        lastSync = new Date(latestSync).getTime();
      }
      
      const isStale = Date.now() - lastSync > options.maxAge;
      
      return {
        data: cachedData,
        isStale,
        source: 'cache',
        timestamp: lastSync
      };
    } catch (error) {
      return {
        data: null,
        isStale: true,
        source: 'cache',
        timestamp: 0,
        error: error instanceof Error ? error.message : 'Cache fetch error'
      };
    }
  }

  /**
   * Fetch data from network with retry logic
   */
  protected async fetchFromNetwork<T>(
    _cacheKey: string,
    networkFetcher: () => Promise<T>,
    options: Required<CacheStrategyOptions>
  ): Promise<CacheStrategyResult<T>> {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
      try {
        const data = await networkFetcher();
        
        return {
          data,
          isStale: false,
          source: 'network',
          timestamp: Date.now()
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Network fetch failed');
        
        if (attempt < options.maxRetries && options.retryOnError) {
          // Wait before retrying with exponential backoff
          const delay = options.retryDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    return {
      data: null,
      isStale: true,
      source: 'network',
      timestamp: Date.now(),
      error: lastError?.message || 'Network fetch failed after retries'
    };
  }

  /**
   * Perform background sync without blocking
   */
  protected async backgroundSync<T>(
    _cacheKey: string,
    _networkFetcher: () => Promise<T>,
    _options: Required<CacheStrategyOptions>
  ): Promise<void> {
    try {
      // Import sync service dynamically to avoid circular dependencies
      const { syncService } = await import('@/services/offline');

      // Only sync if not already syncing to prevent loops
      if (!syncService.getStatus().isSyncing) {
        await syncService.performSync();
      }
    } catch (error) {
      console.warn('Background sync failed:', error);
    }
  }

  /**
   * Revalidate data in background while returning stale data
   */
  protected async revalidateInBackground<T>(
    _cacheKey: string,
    _networkFetcher: () => Promise<T>,
    _options: Required<CacheStrategyOptions>
  ): Promise<void> {
    try {
      // Import sync service dynamically to avoid circular dependencies
      const { syncService } = await import('@/services/offline');

      // Only sync if not already syncing to prevent loops
      if (!syncService.getStatus().isSyncing) {
        await syncService.performSync();
      }
    } catch (error) {
      console.warn('Background revalidation failed:', error);
    }
  }

  /**
   * Invalidate cache for a specific key
   */
  async invalidateCache(tableName: string): Promise<void> {
    try {
      await indexedDBService.offlineDB.clear(tableName);
    } catch (error) {
      console.warn('Cache invalidation failed:', error);
    }
  }

  /**
   * Warm up cache with initial data
   */
  async warmUpCache(): Promise<void> {
    try {
      if (navigator.onLine) {
        // Import sync service dynamically to avoid circular dependencies
        const { syncService } = await import('@/services/offline');

        // Only perform initial load if not already syncing
        if (!syncService.getStatus().isSyncing) {
          await syncService.performInitialLoad();
        }
      }
    } catch (error) {
      console.warn('Cache warm-up failed:', error);
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    totalSize: number;
    itemCount: number;
    lastUpdated: string | null;
    hitRate: number;
  }> {
    try {
      const stats = await indexedDBService.getStats();
      const metadata = await indexedDBService.syncMetadata.getAllMetadata();

      const totalSize = Object.values(stats).reduce((sum, count) => sum + count, 0);
      const lastUpdated = metadata.length > 0
        ? metadata.reduce((latest, meta) => meta.last_sync > latest ? meta.last_sync : latest, '')
        : null;

      return {
        totalSize,
        itemCount: totalSize,
        lastUpdated,
        hitRate: 0.95 // Placeholder - would need actual tracking
      };
    } catch (error) {
      return {
        totalSize: 0,
        itemCount: 0,
        lastUpdated: null,
        hitRate: 0
      };
    }
  }

  /**
   * Clear all cache
   */
  async clearAllCache(): Promise<void> {
    try {
      await indexedDBService.clearAllData();
    } catch (error) {
      console.warn('Failed to clear all cache:', error);
    }
  }

  /**
   * Smart cache eviction based on usage patterns
   */
  async smartEviction(): Promise<void> {
    try {
      // Get cache stats
      const stats = await this.getCacheStats();

      // If cache is getting large, evict old data
      if (stats.totalSize > 10000) { // Arbitrary threshold
        // Evict training sessions older than 90 days


        // This would require additional IndexedDB operations to filter by date

      }
    } catch (error) {
      console.warn('Smart eviction failed:', error);
    }
  }

  /**
   * Preload critical data
   */
  async preloadCriticalData(userId: string): Promise<void> {
    try {
      if (!navigator.onLine) return;

      // Preload essential data in background
      const preloadPromises = [
        // Load recent training sessions
        indexedDBService.trainingSessions.getRecentSessions(userId, 30),
        // Load positions (static data)
        indexedDBService.positions.getAll(),
        // Load user techniques
        indexedDBService.userTechniques.getByUserId(userId),
        // Load roadmap data
        indexedDBService.roadmapMacro.getActive(),
        indexedDBService.roadmapMeso.getActive()
      ];

      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.warn('Critical data preload failed:', error);
    }
  }
}

/**
 * Convenience function for cache-first data fetching
 */
export async function fetchWithCacheFirst<T>(
  cacheKey: string,
  networkFetcher: () => Promise<T>,
  cacheFetcher: () => Promise<T | null>,
  options?: CacheStrategyOptions
): Promise<CacheStrategyResult<T>> {
  // Create a temporary strategy instance for the generic function
  const strategy = new (class extends BaseStrategy {})();
  return strategy.execute(cacheKey, networkFetcher, cacheFetcher, options);
}
